import { useSearchParams } from 'next/navigation';

import type { Channel } from '@/survey-graphql';

import {
  parseResponderEmail,
  resolveResponderChannel,
} from './survey-responder-v2-adapter-responder-contact.helpers';

export interface ResponderContact {
  readonly email: string | null;
  readonly channel: string;
}

// test-exception: both derivations are pure and covered directly in
// survey-responder-v2-adapter-responder-contact.helpers.spec.ts; this hook only
// reads `useSearchParams`, which needs a React renderer (RTL is banned here).
/**
 * The contact and channel a prefilled link carries, read from the same two
 * query params the legacy responder reads so an emailed survey attributes its
 * response identically under V2.
 *
 * `initiateUnifiedSession` also accepts `firstName`, `lastName`, `phone` and
 * `sessionKey`. None is read here on purpose: no link the product issues
 * carries them, so a param name would have to be invented, and an invented one
 * fails silently rather than loudly. `sessionKey` is the screener hand-off and
 * belongs to that ticket.
 *
 * The returned object is rebuilt every render, which is safe because the
 * session hook destructures it into `email`/`channel` scalars before they reach
 * an effect's dependency array — no consumer may keep the object itself.
 */
export function useResponderContact(
  fallbackChannel?: Channel
): ResponderContact {
  const searchParams = useSearchParams();

  return {
    email: parseResponderEmail(searchParams?.get('email') ?? null) ?? null,
    channel: resolveResponderChannel(
      searchParams?.get('channel') ?? null,
      fallbackChannel
    ),
  };
}
